import { Typography } from '@euler/app/components/typography';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import Dialog from '@euler/components/dialog';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import {
  getValidateErrorMessage,
  useFormController,
} from '@euler/lib/form/controller';
import { delayed } from '@euler/utils';
import { usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { useCallback, useState } from 'react';
import { Keyboard } from 'react-native';

export const QuotationDiscountScreen = wrapNavigatorScreen(
  ({
    title,
    message,
    defaultValue,
    onConfirm,
    onCancel,
  }: {
    title: string;
    message?: string;
    defaultValue?: number;
    onConfirm?: (value: number, mode: 'amount' | 'percent') => void;
    onCancel?: () => void;
  }) => {
    const navigation = useNavigation();
    const { rpx } = useResponsiveContext();

    const [visible, setVisible] = useState(true);
    const [mode, setMode] = useState<'amount' | 'percent'>('amount');
    const [error, setError] = useState<string | undefined>();

    const controller = useFormController<{ discount: string }>({
      discount: {
        required: true,
        defaultValue: defaultValue != null ? `${defaultValue}` : '0',
        validateOnChange: false,
        validators: [
          value => {
            value = value.trim();
            if (!value) return true;
            if (!/^\d+(\.\d{1,2})?$/.test(value)) {
              return { error: '请输入正确的优惠金额' };
            }
            if (mode === 'percent' && Number(value) > 100) {
              return { error: '优惠率不能超过100%' };
            }
            return true;
          },
        ],
      },
    });

    const { form } = controller;

    const handleCancel = usePersistFn(() => {
      setVisible(false);
      onCancel?.();
      delayed(() => {
        navigation.goBack();
      }, 250);
    });

    const handleConfirm = usePersistFn(async () => {
      if (!(await controller.validateAll())) {
        return;
      }

      try {
        onConfirm?.(Number(form.discount.value), mode);
        Keyboard.dismiss();
        setVisible(false);
        navigation.goBack();
      } catch (e) {
        setError((e as any).message);
      }
    });

    const onToggleMode = useCallback(() => {
      setMode(x => (x === 'amount' ? 'percent' : 'amount'));
      form.discount.update('0');
    }, [form.discount]);

    return (
      <Dialog.Container
        visible={visible}
        onRequestClose={handleCancel}
        useNativeDriver
      >
        <Dialog.Title>{title}</Dialog.Title>
        {message ? <Dialog.Description>{message}</Dialog.Description> : null}
        <Dialog.Input
          value={`${form.discount.value.trim()}`}
          onChangeText={text => {
            setError(undefined);
            form.discount.update(text.replace(/[^\d.]/g, ''));
          }}
          autoFocus
          onBlur={form.discount.onBlur}
          keyboardType={'decimal-pad'}
          clearButtonMode="always"
          placeholderTextColor={'#999'}
          prefix={
            <Center
              css={`
                flex-shrink: 0;
                width: ${rpx(32)}px;
                background-color: #ddd;
                margin-left: -8px;
              `}
            >
              <Typography.Text bold size={rpx(21)}>
                -
              </Typography.Text>
            </Center>
          }
          suffix={
            <Center.Touchable
              onPress={onToggleMode}
              css={`
                flex-shrink: 0;
                width: ${rpx(32)}px;
                background-color: #ddd;
                margin-right: -8px;
              `}
            >
              <Typography.Text medium size={rpx(16)}>
                {mode === 'amount' ? '¥' : '%'}
              </Typography.Text>
            </Center.Touchable>
          }
          errorMsg={
            form.discount.error
              ? getValidateErrorMessage(form.discount.error)
              : undefined
          }
          style={[
            {
              textAlign: 'center',
              fontWeight: '800',
            },
          ]}
        />
        {error ? (
          <Typography.Text
            color={StatusColors.Danger}
            alignSelf="center"
            marginHorizontal={20}
            mb={20}
            mt={-10}
          >
            {error}
          </Typography.Text>
        ) : null}
        <Dialog.Button label={'取消'} onPress={handleCancel} />
        <Dialog.Button label={'确认'} onPress={handleConfirm} />
      </Dialog.Container>
    );
  },
  {
    presentation: 'transparentModal',
    headerShown: false,
    animationEnabled: false,
    detachPreviousScreen: false,
    cardStyle: { backgroundColor: 'transparent' },
  },
);
